<template>
  <div>
    <template v-if="tipo?.llevaBienes">
      <p class="sw-subtitulo">{{ t('El inmueble o bien') }}</p>
      <v-row dense>
        <v-col cols="12" sm="8">
          <v-text-field v-model="estado.bienes[0].descripcion" :label="t('Descripción o dirección (opcional)')"
            :placeholder="t('Piso en C/ Mayor 1, 3ºB, Murcia')" variant="outlined" density="comfortable" />
        </v-col>
        <v-col cols="12" sm="4">
          <v-text-field v-model="estado.bienes[0].referenciaCatastral" :label="t('Ref. catastral (opcional)')"
            variant="outlined" density="comfortable" />
        </v-col>
      </v-row>
    </template>

    <!-- Cómo prefiere firmar (trámites que se pueden firmar por videoconferencia, si la notaría la ofrece). Ref: https://trello.com/c/dc7kKk4R -->
    <div v-if="preguntarFirma">
      <p class="sw-subtitulo">{{ t('¿Cómo prefiere firmar?') }}</p>
      <div class="sw-segmento sw-segmento--envolver" role="radiogroup" :aria-label="t('¿Cómo prefiere firmar?')">
        <button type="button" role="radio" :aria-checked="!estado.firmaVideoconferencia"
          :class="{ activo: !estado.firmaVideoconferencia }" @click="estado.firmaVideoconferencia = false">
          {{ t('En la notaría') }}
        </button>
        <button type="button" role="radio" :aria-checked="!!estado.firmaVideoconferencia"
          :class="{ activo: !!estado.firmaVideoconferencia }" @click="estado.firmaVideoconferencia = true">
          {{ t('Por videoconferencia') }}
        </button>
      </div>
      <p v-if="estado.firmaVideoconferencia" class="sw-ayuda">
        {{ t('La firma se hace a través del portal oficial del notariado. La notaría le confirmará si su caso puede firmarse por videoconferencia y le explicará los pasos.') }}
      </p>
    </div>

    <!-- Firma: cita que propone el cliente, si la notaría la ofrece para el trámite (ref: https://trello.com/c/Bnl5pAM2) -->
    <div v-if="reglasCita" class="sw-firma">
      <p class="sw-subtitulo">{{ t('¿Cuándo le vendría bien firmar?') }} <span class="sw-tenue">{{ t('(opcional)') }}</span></p>
      <CalendarioCita v-model="estado.cita" :reglas="reglasCita" id="sw-cita" :etiqueta="t('¿Cuándo le vendría bien firmar?')"
        @update:model-value="limpiarError('cita')" />
      <p class="sw-ayuda">{{ avisoCita }}</p>
      <!-- La falta de hora ya la avisa el calendario; aquí, con la clase sw-error para que "Continuar" lleve hasta ella -->
      <p v-if="errores['cita']" class="sw-error" :style="faltaHoraCita ? oculto : undefined" role="alert">{{ errores['cita'] }}</p>
    </div>

    <p class="sw-subtitulo">{{ t('¿Algo más que debamos saber?') }}</p>
    <v-textarea v-model="estado.comentario" :label="t('Comentario (opcional)')" variant="outlined" rows="4" auto-grow
      counter="2000" :error-messages="errores['comentario'] ? [errores['comentario']] : []"
      :placeholder="t('Fechas que le vienen bien, si hay financiación, dudas…')" />

    <!-- Cómo y cuándo prefiere que le contacten (opcional; evita llamadas perdidas) -->
    <p class="sw-subtitulo">{{ t('¿Cómo prefiere que le contactemos?') }} <span class="sw-tenue">{{ t('(opcional)') }}</span></p>
    <div class="sw-preferencias">
      <div class="sw-segmento sw-segmento--envolver" role="radiogroup" :aria-label="t('¿Cómo prefiere que le contactemos?')">
        <button v-for="m in medios" :key="m.valor" type="button" role="radio" :aria-checked="pref.medio === m.valor"
          :class="{ activo: pref.medio === m.valor }" @click="pref.medio = pref.medio === m.valor ? '' : m.valor">
          <v-icon size="18">{{ m.icono }}</v-icon>{{ m.texto }}
        </button>
      </div>
      <div class="sw-segmento sw-segmento--envolver" role="radiogroup" :aria-label="t('¿En qué horario?')">
        <button v-for="f in franjas" :key="f.valor" type="button" role="radio" :aria-checked="pref.franja === f.valor"
          :class="{ activo: pref.franja === f.valor }" @click="pref.franja = pref.franja === f.valor ? '' : f.valor">
          {{ f.texto }}
        </button>
      </div>
    </div>

    <!-- Presupuesto previo (solo si la notaría lo ofrece) -->
    <div v-if="config.PermitirPresupuesto" class="sw-presupuesto">
      <v-checkbox v-model="estado.quierePresupuesto" hide-details density="comfortable" color="primary">
        <template #label>
          <span>
            <strong>{{ t('Quiero un presupuesto antes de empezar') }}</strong><br />
            <span class="sw-tenue">{{ t('La notaría le enviará el importe y podrá aceptarlo antes de que preparen su encargo.') }}</span>
          </span>
        </template>
      </v-checkbox>
    </div>

    <div v-if="tipo && (tipo.documentos.length || config.PermitirAdjuntos)" class="sw-documentos">
      <div class="sw-documentos__cabecera">
        <p class="sw-subtitulo">{{ t('Documentación') }}</p>
        <span v-if="config.PermitirAdjuntos && documentosTramite.length" class="sw-contador"
          :class="{ 'sw-contador--completo': aportados === documentosTramite.length }">
          {{ t('Ha adjuntado {n} de {total} documentos', { n: aportados, total: documentosTramite.length }) }}
        </span>
      </div>
      <p class="sw-ayuda">
        <template v-if="config.PermitirAdjuntos">
          {{ t('Puede adjuntarla ahora o aportarla más adelante, cuando la notaría le contacte. PDF, JPG o PNG.') }}
        </template>
        <template v-else>{{ t('No tiene que aportarla ahora; la notaría se la pedirá cuando le contacte.') }}</template>
      </p>

      <ul>
        <li v-for="d in documentos" :key="d.nombre" class="sw-doc">
          <div class="sw-doc__fila">
            <v-icon size="20" :color="archivosDe(claveDoc(d)).length ? 'success' : undefined">
              {{ archivosDe(claveDoc(d)).length ? 'mdi-check-circle' : 'mdi-file-document-outline' }}</v-icon>
            <span class="sw-doc__nombre">{{ d.nombre }}</span>
            <span v-if="!d.obligatorio" class="sw-etiqueta">{{ t('Si procede') }}</span>
            <v-spacer />
            <v-btn v-if="config.PermitirAdjuntos && quedanHuecos" size="default" variant="tonal" prepend-icon="mdi-paperclip"
              class="sw-adjuntar" :disabled="preparando" @click="elegir(claveDoc(d))">{{ t('Adjuntar') }}</v-btn>
          </div>
          <div v-for="(a, i) in archivosDe(claveDoc(d))" :key="a.uid" class="sw-archivo">
            <span class="sw-archivo__nombre">{{ a.file.name }}</span>
            <span class="sw-tenue">{{ tamano(a.file.size) }}</span>
            <template v-if="archivosDe(claveDoc(d)).length > 1">
              <v-btn icon="mdi-arrow-up" size="x-small" variant="text" :disabled="i === 0"
                :aria-label="`${t('Mover arriba')}: ${a.file.name}`" @click="moverAdjunto(a.uid, -1)" />
              <v-btn icon="mdi-arrow-down" size="x-small" variant="text" :disabled="i === archivosDe(claveDoc(d)).length - 1"
                :aria-label="`${t('Mover abajo')}: ${a.file.name}`" @click="moverAdjunto(a.uid, 1)" />
            </template>
            <v-btn icon="mdi-close" size="x-small" variant="text" :aria-label="`Quitar ${a.file.name}`"
              @click="quitarAdjunto(a.uid)" />
          </div>
        </li>
      </ul>

      <p v-if="config.PermitirAdjuntos && hayVariasFotos" class="sw-ayuda">
        {{ t('Si adjunta varias fotos de un mismo documento, la notaría las recibirá unidas en un solo PDF, en el orden que indique aquí.') }}
      </p>
      <p v-if="preparando" class="sw-ayuda" role="status">
        <v-progress-circular indeterminate size="16" width="2" class="mr-2" />{{ t('Preparando archivo…') }}
      </p>
      <p v-if="avisoAdjuntos" class="sw-error" role="alert">{{ avisoAdjuntos }}</p>
      <p v-if="config.PermitirAdjuntos && adjuntos.length" class="sw-ayuda">
        {{ t('{n} de {max} archivos · {usado} de {limite} MB.', { n: adjuntos.length, max: ADJUNTOS.maxArchivos, usado: tamano(totalBytes), limite: megas(ADJUNTOS.maxBytesTotal) }) }}
        {{ t('Si recarga la página tendrá que volver a adjuntarlos.') }}
      </p>

      <input ref="inputRef" type="file" class="sw-oculto" :accept="ADJUNTOS.accept" multiple @change="onArchivos">
    </div>
  </div>
</template>

<script setup lang="ts">
import { t } from '../../composables/solicitudTextos'
import { computed, ref } from 'vue'
import { ADJUNTOS, megas, useSolicitudWeb } from '../../composables/useSolicitudWeb'
import CalendarioCita from '../elements/CalendarioCita.vue'

const { estado, tipo, errores, adjuntos, config, anadirAdjuntos, quitarAdjunto, moverAdjunto, preguntarFirma, reglasCita, avisoCita, citaValida, limpiarError } = useSolicitudWeb()

// Una cita elegida con otro trámite (u otro día) puede no valer con las reglas de ahora: se deja lo que sigue valiendo
if (estado.value.cita?.fecha) estado.value.cita = citaValida(estado.value.cita, reglasCita.value)
// Sin hora en un trámite de día y hora: el aviso visible lo pone el calendario; el error queda oculto a la vista
const faltaHoraCita = computed(() => reglasCita.value?.modo === 'DIA_HORA' && !!estado.value.cita?.fecha && !estado.value.cita?.hora)
const oculto = { position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)' } as const

// Borradores anteriores a esta versión no traen la preferencia de contacto
if (!estado.value.contactoPreferido) estado.value.contactoPreferido = { medio: '', franja: '' }
const pref = computed(() => estado.value.contactoPreferido!)
const medios = computed(() => [
  { valor: 'TELEFONO', texto: t('Teléfono'), icono: 'mdi-phone-outline' },
  { valor: 'EMAIL', texto: t('Email'), icono: 'mdi-email-outline' },
  { valor: 'WHATSAPP', texto: t('WhatsApp'), icono: 'mdi-whatsapp' }
] as const)
const franjas = computed(() => [
  { valor: 'MANANA', texto: t('Por la mañana') },
  { valor: 'TARDE', texto: t('Por la tarde') },
  { valor: 'INDIFERENTE', texto: t('Me da igual') }
] as const)

const inputRef = ref<HTMLInputElement | null>(null)
const documentoActual = ref('')
const avisoAdjuntos = ref<string | null>(null)

const OTRO = 'Otro documento'
const documentos = computed(() => {
  const lista = [...(tipo.value?.documentos ?? [])]
  if (config.value.PermitirAdjuntos) lista.push({ nombre: t(OTRO), nombreEs: OTRO, obligatorio: false })
  return lista
})
/** Documentos del trámite (sin "Otro documento") y cuántos tienen ya algún archivo. */
const documentosTramite = computed(() => tipo.value?.documentos ?? [])
const aportados = computed(() => documentosTramite.value.filter((d) => archivosDe(claveDoc(d)).length > 0).length)
const quedanHuecos = computed(() => adjuntos.value.length < ADJUNTOS.maxArchivos)
/** Hay algún documento con más de una foto (se unirán en un PDF). */
const hayVariasFotos = computed(() => {
  const cuenta: Record<string, number> = {}
  for (const a of adjuntos.value) {
    if (a.file.type === 'application/pdf') continue
    cuenta[a.documento] = (cuenta[a.documento] ?? 0) + 1
    if (cuenta[a.documento] > 1) return true
  }
  return false
})
const totalBytes = computed(() => adjuntos.value.reduce((s, a) => s + a.file.size, 0))

/** Los adjuntos se guardan con el nombre del documento en español (la notaría trabaja en español). */
function claveDoc(d: { nombre: string; nombreEs?: string }) {
  return d.nombreEs || d.nombre
}

function archivosDe(documento: string) {
  return adjuntos.value.filter((a) => a.documento === documento)
}

function elegir(documento: string) {
  documentoActual.value = documento
  avisoAdjuntos.value = null
  inputRef.value?.click()
}

const preparando = ref(false)

async function onArchivos(e: Event) {
  const input = e.target as HTMLInputElement
  const archivos = input.files?.length ? Array.from(input.files) : []
  input.value = '' // permite volver a elegir el mismo archivo
  if (!archivos.length) return
  preparando.value = true
  try {
    avisoAdjuntos.value = await anadirAdjuntos(archivos, documentoActual.value)
  } finally {
    preparando.value = false
  }
}

function tamano(bytes: number) {
  return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
</script>
